import {
    DataLayerEventName,
    PagePosition,
    PlayerEvent,
    VideoSourceMeta,
} from '@news-mono/web-common'

interface BuildAdPayloadParams {
    position: number
    pagePosition: PagePosition
    isMuted: boolean
    isPaused: boolean
    width: number
    height: number
}

export const buildAdEventPayload = ({
    position,
    pagePosition,
    isMuted,
    isPaused,
    width,
    height,
}: BuildAdPayloadParams): PlayerEvent['payload'] => {
    const video: VideoSourceMeta = {
        id: 'vertical-ad',
        name: 'Ad',
        duration: 0,
        width,
        height,
        source: 'ad',
        displaySource: 'ad',
        pubdate: '',
    }

    return {
        playlistType: 'not-playlist-video',
        position,
        muted: isMuted,
        volume: isMuted ? 0 : 1,
        paused: isPaused,
        pagePosition,
        video,
        videoPlayerVariant: 'Autoplay',
    }
}

export type AdEventType =
    | DataLayerEventName.adRequest
    | DataLayerEventName.adLoad
    | DataLayerEventName.adStart
    | DataLayerEventName.adPlay
    | DataLayerEventName.adPause
    | DataLayerEventName.adEnd
    | DataLayerEventName.adError
    | DataLayerEventName.adVolumeChange
    | DataLayerEventName.adClick
    | DataLayerEventName.adMilestone25
    | DataLayerEventName.adMilestone50
    | DataLayerEventName.adMilestone75
    | DataLayerEventName.adPodComplete

export type AdPlayerEvent = Extract<PlayerEvent, { type: AdEventType }>

interface CreateAdEventEmitterParams {
    onEvent?: (event: PlayerEvent) => void
    position: number
    pagePosition: PagePosition
    getMuted: () => boolean
    getPaused: () => boolean
    getSize: () => { width: number; height: number }
}

export const createAdEventEmitter = ({
    onEvent,
    position,
    pagePosition,
    getMuted,
    getPaused,
    getSize,
}: CreateAdEventEmitterParams) => {
    return (type: AdEventType, extra?: Record<string, unknown>) => {
        if (!onEvent) return
        const { width, height } = getSize()
        const basePayload = buildAdEventPayload({
            position,
            pagePosition,
            isMuted: getMuted(),
            isPaused: getPaused(),
            width,
            height,
        })
        const event = {
            type,
            originator: 'player-event',
            payload: {
                ...basePayload,
                ...(extra ?? {}),
            },
        } as AdPlayerEvent
        onEvent(event)
    }
}
